import type { OrderPackage, PackageInfo } from '../domain/types';
import type { OrderPackageDto, PackageInfoDto } from './types';

/**
 * Hranice mezi API a doménou.
 *
 * Od v2 je to skoro jen přejmenování — kontrakt už posílá data ve tvaru,
 * v jakém je UI potřebuje. Vrstva ale zůstává: díky ní se změna DTO neprojeví
 * napříč komponentami a doména si drží vlastní pravidla (např. že `weightKg`
 * chybí jako `null`, ne jako nula).
 *
 * Ruční parsování `vehicle` a odvozování převozu z textu `transportMessage`
 * zmizelo — v2 posílá `vehicleCode` / `vehicleName` / `plate` a `transfer`
 * jako strukturovaný blok.
 */
export function mapPackage(dto: PackageInfoDto): PackageInfo {
  return {
    scannedCode: dto.shipment.scannedCode,
    shipmentId: dto.shipment.shipmentId,
    shipmentNumber: dto.shipment.shipmentNumber,

    statusCode: dto.status.code ?? null,
    statusLabelRaw: dto.status.labelCs,

    sequence: dto.shipment.sequence ?? null,
    scanCount: dto.shipment.scanCount,

    deliverAt: dto.dates.deliverAt ?? null,
    expeditionAt: dto.dates.expeditionAt ?? null,

    transfer: dto.transfer
      ? {
          direction: dto.transfer.direction,
          warehouseCode: dto.transfer.warehouseCode,
          confirmed: dto.transfer.confirmed,
        }
      : null,

    pick: {
      driver: dto.pick.driver ?? null,
      routeNumber: dto.pick.routeNumber ?? null,
      vehicleName: joinVehicle(dto.pick.vehicleCode, dto.pick.vehicleName),
      plate: dto.pick.plate ?? null,
    },

    recipient: {
      name: dto.recipient.name ?? null,
      note: dto.recipient.note ?? null,
      street: dto.recipient.street ?? null,
      city: dto.recipient.city ?? null,
      zip: dto.recipient.zip ?? null,
      lat: dto.recipient.lat ?? null,
      lon: dto.recipient.lon ?? null,
    },

    zone: dto.zone ?? null,
    weightKg: dto.shipment.weightKg ?? null,
    deliveryTime: dto.shipment.deliveryTime ?? null,

    // Bez bloku `order` zůstane `packages` null – doména pak pás kompletace
    // i složení teplot schová. Půlka pravdy by se vykreslila jako celá.
    order: dto.order
      ? {
          packageIndex: dto.order.packageIndex ?? null,
          packages: mapOrderPackages(dto.order.packages),
        }
      : { packageIndex: null, packages: null },

    temperature: dto.temperature ?? null,

    flags: {
      isRetryAttempt: dto.flags.isRetryAttempt,
      isSecondPart: dto.flags.isSecondPart,
    },
  };
}

/**
 * Balíky objednávky.
 *
 * Prázdné pole není „nula balíků“ – je to chybějící informace. Kontrakt říká,
 * že `packages` se posílá neprázdné, jinak se neposílá celý blok `order`;
 * kdyby přesto dorazilo prázdné nebo úplně chybělo, chováme se k tomu stejně
 * jako k chybějícímu bloku. Jinak by karta hlásila „Počet balíků 0“ a pás
 * kompletace by zmizel bez vysvětlení.
 */
function mapOrderPackages(packages: OrderPackageDto[] | undefined | null): OrderPackage[] | null {
  if (!Array.isArray(packages) || packages.length === 0) return null;

  return packages.map((p) => ({
    number: p.number,
    temperature: p.temperature ?? null,
    scannedAt: p.scannedAt ?? null,
  }));
}

/** `L12` + `Renault Master` → `L12 · Renault Master`. */
function joinVehicle(code: string | null | undefined, name: string | null | undefined): string | null {
  if (code && name) return `${code} · ${name}`;
  return code ?? name ?? null;
}
